import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Chip,
  Badge,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import InventoryIcon from '@mui/icons-material/Inventory';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import BuildIcon from '@mui/icons-material/Build';
import EventIcon from '@mui/icons-material/Event';
import EngineeringIcon from '@mui/icons-material/Engineering';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { EmptyState } from '@/components/common/EmptyState';
import { useMemo } from 'react';
import type { DashboardAlert, AlertSeverity, AlertType } from '@/hooks';

/** Severity sort order — critical first, then warning, then info. */
const SEVERITY_ORDER: Record<AlertSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

const severityIcons: Record<AlertSeverity, React.ReactNode> = {
  critical: <ErrorIcon sx={{ color: 'error.main' }} />,
  warning: <WarningIcon sx={{ color: 'warning.main' }} />,
  info: <InfoIcon sx={{ color: 'primary.light' }} />,
};

const typeIcons: Record<AlertType, React.ReactNode> = {
  low_inventory: <InventoryIcon />,
  overdue_approval: <ThumbUpIcon />,
  overdue_work_order: <BuildIcon />,
  unconfirmed_booking: <EventIcon />,
  equipment_maintenance: <EngineeringIcon />,
};

interface AlertsWidgetProps {
  data?: DashboardAlert[];
}

export function AlertsWidget({ data: alerts = [] }: AlertsWidgetProps) {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();

  const sortedAlerts = useMemo(
    () => [...alerts].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]),
    [alerts],
  );

  const severityColors: Record<AlertSeverity, string> = {
    critical: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.primary.light,
  };

  // Count by severity
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const warningCount = alerts.filter((a) => a.severity === 'warning').length;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{t('alerts')}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {criticalCount > 0 && (
                <Chip
                  size="small"
                  label={criticalCount}
                  sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), color: 'error.main', fontWeight: 600 }}
                />
              )}
              {warningCount > 0 && (
                <Chip
                  size="small"
                  label={warningCount}
                  sx={{ bgcolor: alpha(theme.palette.warning.main, 0.08), color: 'warning.main', fontWeight: 600 }}
                />
              )}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('alertsRequiringAttention')}
          </Typography>
        </Box>

        <Divider />

        {sortedAlerts.length === 0 ? (
          <Box sx={{ flex: 1 }}>
            <EmptyState
              icon={<CheckCircleIcon />}
              title={t('noAlerts')}
              description={t('allClear')}
              minHeight={250}
            />
          </Box>
        ) : (
          <List sx={{ flex: 1, overflow: 'auto', minHeight: 0, py: 0 }}>
            {sortedAlerts.map((alert, index) => (
              <Box key={alert.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    {...(alert.link ? { component: RouterLink, to: alert.link } : {})}
                    sx={{
                      py: 1.5,
                      borderLeft: 3,
                      borderColor: severityColors[alert.severity],
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': {
                        bgcolor: alpha(severityColors[alert.severity], 0.03),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Badge
                        badgeContent={alert.count}
                        color={alert.severity === 'critical' ? 'error' : 'warning'}
                        invisible={!alert.count}
                        max={99}
                      >
                        <Box sx={{ color: severityColors[alert.severity] }}>
                          {typeIcons[alert.type]}
                        </Box>
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {t(alert.titleKey, alert.titleParams)}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {alert.descriptionKey
                            ? t(alert.descriptionKey, alert.descriptionParams)
                            : (alert.descriptionText ?? '')}
                        </Typography>
                      }
                    />
                    {severityIcons[alert.severity]}
                  </ListItemButton>
                </ListItem>
                {index < sortedAlerts.length - 1 && <Divider component="li" />}
              </Box>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
